import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Badge,
	Box,
	Button,
	Divider,
	IconButton,
	Stack,
	Typography,
} from "@mui/material";
import {
	CircleNotificationsOutlined,
	ExpandMore,
	OpenInNew,
} from "@mui/icons-material";
import { ESRThunkDispatch, updateNotificationStatus } from "../../actions/form";
import { ESRSummary } from "./ESRSummary";
import { Fragment, useState } from "react";
import dayjs from "dayjs";
import { ESRNotificationT } from "../../types/data";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import relativeTime from "dayjs/plugin/relativeTime";
import { download } from "../../request";
import getBackendURL from "../../api/api";

dayjs.extend(relativeTime);

function colorForImportance(importance: number) {
	switch (importance) {
		case 2:
			return "#F72A2A";
		case 1:
			return "#18CE41";
		default:
			return "#C7C7C7";
	}
}

type Props = {
	esrId: string;
	notifications: ESRNotificationT[];
};

export function ESRNotifications({ esrId, notifications }: Props) {
	const dispatch = useDispatch<ESRThunkDispatch>();
	const navigate = useNavigate();
	const [expanded, setExpanded] = useState(false);

	return (
		<Accordion
			key={esrId}
			expanded={expanded}
			onChange={(_, willBeExpanded) => {
				setExpanded(willBeExpanded);
			}}
		>
			<AccordionSummary expandIcon={<ExpandMore />}>
				<Stack
					direction="row"
					sx={{ width: "100%", alignItems: "center", gap: "16px" }}
				>
					{notifications?.length > 0 && (
						<>
							<Box sx={{ width: 20, flex: "none" }}>
								<Badge
									color="error"
									badgeContent={
										notifications?.filter(
											(notif) => notif.status === 0 && !notif.invalid,
										).length
									}
								>
									<CircleNotificationsOutlined />
								</Badge>
							</Box>
							<Typography sx={{ width: 100, fontWeight: "bold" }}>
								{notifications![0].esr.addendum_id.length === 0 ? (
									<>ESR #{notifications![0].esr.id || "-"}</>
								) : (
									<>
										ESR #{notifications![0].esr.addendum_id}
										{notifications[0].esr.addendum_label}
									</>
								)}
							</Typography>
							{/*TODO: the same ESR info doesn't need to be attached to every notification*/}
							<ESRSummary esr={notifications![0].esr} />
							<Box sx={{ flex: 1 }} />
							<IconButton
								aria-label={`View details for ESR ${notifications![0].esr.addendum_id.length === 0 ? notifications![0].esr.id : notifications![0].esr.addendum_id + notifications![0].esr.addendum_label}`}
								onClick={(e) => {
									e.stopPropagation();
									notifications![0].esr.addendum_id.length === 0 ?
												navigate(`/esr/${notifications![0].esr.id}/initial/${notifications![0].esr.id}`,) :
												navigate(`/esr/${notifications![0].esr.addendum_id}/${notifications![0].esr.addendum_label}/${notifications![0].esr.id}`)
								}}
							>
								<OpenInNew />
							</IconButton>
						</>
					)}
				</Stack>
			</AccordionSummary>
			<AccordionDetails>
				{notifications?.map((notif) => {
					return (
						<Fragment key={notif.id}>
							<Divider sx={{ flex: 1, my: "8px" }} />
							<Stack
								direction="row"
								sx={{
									overflow: "hidden",
									alignItems: "center",
									my: "8px",
									gap: "16px",
								}}
							>
								<Typography sx={{ fontSize: 12, flex: "none" }}>
									{dayjs(notif.timestamp).format("MM-DD-YYYY")}
								</Typography>
								<Divider orientation="vertical" sx={{ height: "20px" }} />
								<Box
									sx={{
										flex: "none",
										width: 6,
										height: 6,
										borderRadius: 3,
										background: colorForImportance(notif.importance),
									}}
								/>
								<Typography
									fontWeight={notif.invalid || notif.status > 0 ? "normal" : "bold"}
									fontSize={16}
									my="8px"
									sx={{
										cursor: "pointer",
										whiteSpace: "pre-wrap",
										...(notif.invalid && {
											color: "text.disabled",
											textDecoration: "line-through",
										}),
									}}
									onClick={(e) => {
										e.stopPropagation();
										if (notif.status === 0)
											dispatch(updateNotificationStatus(notif.id, 1));
										(notif.link_type.startsWith("qid:") || notif.link_type.length > 0) ? (
											notif.esr.addendum_id.length === 0 ?
												navigate(`/esr/${notif.esr.id}/initial/${notif.esr.id}/redirect/${notif.link_type.replace("qid:", "")}`,) :
												navigate(`/esr/${notif.esr.addendum_id}/${notif.esr.addendum_label}/${notif.esr.id}/redirect/${notif.link_type.replace("qid:", "")}`)
										) : (
											notif.esr.addendum_id.length === 0 ?
												navigate(`/esr/${notif.esr.id}/initial/${notif.esr.id}`,) :
												navigate(`/esr/${notif.esr.addendum_id}/${notif.esr.addendum_label}/${notif.esr.id}`)
										)
									}}
								>
									{notif.display_name}
								</Typography>

								<Divider orientation="vertical" sx={{ height: "20px" }} />
								{notif.deadline && (
									<Typography
										style={{
											whiteSpace: "nowrap",
											overflow: "hidden",
											textOverflow: "ellipsis",
											fontSize: 12,
											color:
												dayjs(notif.timestamp).diff(dayjs()) < 0
													? "red"
													: "black",
										}}
									>
										Due {dayjs(notif.deadline).fromNow()}
									</Typography>
								)}
								<Box sx={{ flex: "auto" }} />
								{notif.file_id ? (
									<Button
										sx={{ flex: "none" }}
										onClick={() =>
											download(
												getBackendURL(
													`/esr/question_attachments/?esr_id=${esrId}&question_id=${notif.link_type.replace("qid:", "")}&notification_id=${notif.id}`,
												),
											)
										}
									>
										Download File
									</Button>
								) : null}
								{notif.status > 0 && !notif.invalid && (
									<Button
										sx={{
											flex: "none",
											overflow: "hidden",
										}}
										onClick={() =>
											dispatch(updateNotificationStatus(notif.id, 0))
										}
									>
										Mark Unread
									</Button>
								)}
								{(notif.status === 1 || (notif.invalid && notif.status === 0)) && (
									<Button
										variant="contained"
										sx={{
											flex: "none",
											overflow: "hidden",
										}}
										onClick={() =>
											dispatch(updateNotificationStatus(notif.id, 2))
										}
									>
										Hide
									</Button>
								)}
								{notif.status === 2 && (
									<Button
										variant="contained"
										sx={{
											flex: "none",
											overflow: "hidden",
										}}
										onClick={() =>
											dispatch(updateNotificationStatus(notif.id, 1))
										}
									>
										Unhide
									</Button>
								)}
								<IconButton
									aria-label={`View details for ESR ${esrId}, notification "${notif.display_name}"`}
									sx={{ flex: "none"}}
									onClick={(e) => {
										e.stopPropagation();
										if (notif.status === 0)
											dispatch(updateNotificationStatus(notif.id, 1));
										(notif.link_type?.startsWith("qid:") || notif.link_type?.length > 0) ? (
											notif.esr.addendum_id.length === 0 ?
												navigate(`/esr/${notif.esr.id}/initial/${notif.esr.id}/redirect/${notif.link_type.replace("qid:", "")}`,) :
												navigate(`/esr/${notif.esr.addendum_id}/${notif.esr.addendum_label}/${notif.esr.id}/redirect/${notif.link_type.replace("qid:", "")}`)
										) : (
											notif.esr.addendum_id.length === 0 ?
												navigate(`/esr/${notif.esr.id}/initial/${notif.esr.id}`,) :
												navigate(`/esr/${notif.esr.addendum_id}/${notif.esr.addendum_label}/${notif.esr.id}`)
										)
									}}
								>
									<OpenInNew />
								</IconButton>
							</Stack>
						</Fragment>
					);
				})}
			</AccordionDetails>
		</Accordion>
	);
}